/**
 * Geo Intent & URL parser for WhatsApp, SMS, and Google Maps deep links.
 * Extracts geographic coordinates, place labels, or query strings from standard geo URIs and Maps URLs.
 */
import toast from 'react-hot-toast';
import { parseGeoUri } from './parseGeoUri';
import { translations, formatT } from '../i18n/translations';

// Re-exported so existing `import { parseGeoUri } from './geoIntent'` keeps
// working. The parser itself now lives in a dependency-free module so it can be
// unit-tested without pulling in react-hot-toast (see parseGeoUri.js).
export { parseGeoUri };

// This module runs outside React (deep-link handler), so it reads the rider's
// saved language directly instead of using the useLanguage() hook.
const tr = (key, vars) => {
  let lang = 'en';
  try { lang = localStorage.getItem('mytownly_rides_lang') === 'te' ? 'te' : 'en'; } catch { /* */ }
  const lookup = (l) => key.split('.').reduce((n, k) => n?.[k], translations[l]);
  return formatT(lookup(lang) || lookup('en') || '', vars);
};

const isNativeApp = () =>
  typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

/**
 * Handle incoming geo intent / link, resolve location, and dispatch to booking screen.
 */
export const handleGeoLink = async (rawUrl, navigate) => {
  const parsed = parseGeoUri(rawUrl);
  if (!parsed) return;

  let lat = parsed.lat;
  let lng = parsed.lng;
  let name = parsed.name || '';
  let address = '';

  let mapsModule = null;
  try {
    mapsModule = await import('../services/maps');
  } catch { /* */ }

  // If query string was provided without direct coords, forward geocode it
  if ((lat == null || lng == null) && parsed.query && mapsModule?.forwardGeocode) {
    try {
      const geocoded = await mapsModule.forwardGeocode(parsed.query);
      if (geocoded?.lat) {
        lat = geocoded.lat;
        lng = geocoded.lng;
        name = geocoded.name || parsed.query;
        address = geocoded.address || '';
      }
    } catch {
      /* could not forward-geocode */
    }
  }

  if (lat == null || lng == null) return;

  // Boundary check
  if (mapsModule?.isWithinServiceArea && !mapsModule.isWithinServiceArea(lat, lng)) {
    toast.error(tr('book.sharedOutside'), { id: 'geo-outside', duration: 4500 });
    return;
  }

  // Reverse geocode if address is missing
  if ((!name || !address) && mapsModule?.reverseGeocode) {
    try {
      const rev = await mapsModule.reverseGeocode(lat, lng);
      if (rev) {
        name = name || rev.name || 'Shared Location';
        address = address || rev.address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      }
    } catch {
      name = name || 'Shared Location';
      address = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }
  }

  const dropPlace = {
    lat,
    lng,
    name: name || 'Shared Location',
    address: address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    source: 'shared_link',
  };

  toast.success(tr('book.sharedDestSet', { name: name || tr('book.sharedLocation') }), { id: 'geo-set' });

  // Dispatch custom window event so Book.js can capture it instantly even if already mounted
  window.dispatchEvent(new CustomEvent('mt_external_drop', { detail: dropPlace }));

  // Navigate to '/' with state so Book.js receives it on fresh mount
  if (typeof navigate === 'function') {
    navigate('/', { state: { externalDrop: dropPlace } });
  }
};

/**
 * Initialize native Android intent listener for shared locations.
 */
export const initGeoDeepLinking = (navigate) => {
  if (!isNativeApp()) return () => {};

  let cleanup = () => {};

  import('@capacitor/app').then(({ App }) => {
    const listener = App.addListener('appUrlOpen', (event) => {
      if (event?.url) {
        handleGeoLink(event.url, navigate);
      }
    });

    cleanup = () => {
      listener.then((h) => h.remove()).catch(() => {});
    };
  }).catch(() => {});

  return () => {
    cleanup();
  };
};
